


















































































































































import Vue from "vue";
import moment from "moment";
import { computed, ref, watch } from "@vue/composition-api";
import Modal from "@/components/root/modal/Modal.vue";
import SkeletonLoaderItem from "@/components/SkeletonLoaderItem.vue";
import { gen_ai_modal_config } from "./modal.config";
import GaIcon from "../card-items/GaIcon.vue";
import GaSelect from "./events/GaSelect.vue";
import { useGenAiPalette } from "../data/design";
import { INFO_CATEGORIES, InfoCategoryId } from "../data/model";
import { show_gen_ai_events } from "../data/drilldown";
import { PackageEmployee, load_employees } from "../data/package-data";
import { SharedState } from "@/common/state";
import { gen_ai_mock_enabled } from "../data/mock-mode";
import { gen_ai_interaction_labels } from "../data/state";
import { InteractionEmployee, get_interaction_employees } from "../data/api";
import { LevelKey } from "../data/virtual-labels";

/** pages of the employee list read before stopping: 2,000+ people at most */
const MAX_PAGES = 20;
/** as gen-ai-ui prints event times: "Sep 6, 2026 - 10:06 PM" */
const DATE_FORMAT = "MMM D, YYYY - h:mm A";

/**
 * Every employee behind a Widget 5 row, live: tier2count by Employee over the
 * row's labels (api.ts), paged to the end so the table is the whole list, not
 * its first page. Each row carries the department and the first and last
 * interaction, so the table is complete when the list is.
 */
const load_live_employees = async (
  labels: string[],
  category: InfoCategoryId
): Promise<PackageEmployee[]> => {
  const frame = SharedState.calculatedTimeFrame.value.frame;
  const people: InteractionEmployee[] = [];
  let cursor = "";
  for (let page = 0; page < MAX_PAGES; page++) {
    const res = await get_interaction_employees({ labels, frame, cursor });
    if (!res.success) break;
    people.push(...res.value.objects);
    if (!res.value.objects.length || !res.value.cursor || res.value.cursor === cursor) break;
    cursor = res.value.cursor;
  }
  const when = (date: string) => (date ? moment(date).format(DATE_FORMAT) : "");
  return people.map((p) => ({
    id: p.id,
    name: p.name,
    email: p.email,
    department: p.department,
    count: p.count,
    category,
    files: p.files,
    first_event: when(p.first_event),
    last_event: when(p.last_event),
  }));
};

/**
 * The people behind a "Employee AI Interactions" row. The card always opens
 * it on one category; the dropdown switches between the six. There is no
 * "All categories": the counts are per category's virtual labels, and a
 * person's interactions under several categories would be added up.
 */
export default Vue.extend({
  components: { Modal, GaIcon, GaSelect, SkeletonLoaderItem },
  props: ["modalData"],
  setup(props) {
    const config = gen_ai_modal_config(90, 60);
    const T = useGenAiPalette();

    const data = computed(() => props.modalData as any);
    const category = ref<InfoCategoryId>(
      (data.value.category as InfoCategoryId) || INFO_CATEGORIES[0].id
    );
    const category_options = INFO_CATEGORIES.map((c) => ({ value: c.id, label: c.name }));
    const category_name = computed(() => {
      const c = INFO_CATEGORIES.find((d) => d.id === category.value);
      return c ? c.name : "";
    });
    /** the sensitivity levels the card's slider included */
    const levels = computed<LevelKey[]>(
      () => data.value.levels || ["high", "medium", "low", "none"]
    );

    const employees = ref<PackageEmployee[]>([]);
    const loading = ref(true);
    let generation = 0;

    const load = async (cat: InfoCategoryId) => {
      const gen = ++generation;
      loading.value = true;
      let rows: PackageEmployee[] = [];
      // the demo package stands in only in mock mode
      if (gen_ai_mock_enabled()) {
        rows = await load_employees(cat);
      } else {
        const labels = gen_ai_interaction_labels(cat, levels.value);
        rows = labels.length ? await load_live_employees(labels, cat) : [];
      }
      if (gen !== generation) return;
      employees.value = rows;
      loading.value = false;
    };
    watch(category, (cat) => load(cat));

    const on_category = (value: string) => {
      category.value = value as InfoCategoryId;
    };

    /** "—" when the data gives no date or department */
    const first_of = (emp: PackageEmployee) => emp.first_event || "—";
    const last_of = (emp: PackageEmployee) => emp.last_event || "—";
    const department_of = (emp: PackageEmployee) => emp.department || "";

    // category and person both name the list, per the spec: "<info category>
    // <emp name> AI Activities"
    const show_events = (emp: PackageEmployee) =>
      show_gen_ai_events(
        `${category_name.value} <b>${emp.name}</b> AI Activities`.trim(),
        {
          widget: "risk-category",
          category: category.value,
          actor: emp.email,
          actor_id: emp.id,
        },
        // the category and the person are both fixed by how this was opened
        { ai_activities: true }
      );

    const headers = [
      { label: "" },
      { label: "Name" },
      { label: "Email" },
      { label: "Department" },
      { label: "Interactions" },
      { label: "First event", hint: "The employee's first AI interaction in the period" },
      { label: "Last event", hint: "The employee's latest AI interaction in the period" },
    ];

    const export_csv = () => {
      const quote = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
      const lines = [
        ["Name", "Email", "Department", "Interactions Count", "First event", "Last event", "Category"],
        ...employees.value.map((e) => [
          e.name,
          e.email,
          department_of(e),
          e.count,
          e.first_event,
          e.last_event,
          category_name.value,
        ]),
      ];
      const csv = lines.map((r) => r.map(quote).join(",")).join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `employee-ai-interactions-${category.value}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    };

    const head = computed(() => ({
      position: "sticky",
      top: 0,
      zIndex: 20,
      padding: "8px 12px",
      fontSize: "12px",
      fontWeight: 600,
      textAlign: "left",
      color: T.value.ev.strong,
      background: T.value.ev.bg,
    }));
    const cell = computed(() => ({ fontSize: "12px", color: T.value.ev.body }));

    load(category.value);

    return {
      config,
      T,
      category,
      category_options,
      on_category,
      employees,
      loading,
      headers,
      head,
      cell,
      first_of,
      last_of,
      department_of,
      show_events,
      export_csv,
    };
  },
});
